import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { isNull } from 'lodash';
import { saveAs } from 'file-saver';
import lzSCommonsLastErrorMessageAtom from '@lz-atoms/Commons/lastErrorMessage';
import lzSCommonsMediaBrowserDataAtom from '@lz-atoms/Commons/mediaBrowserData';
import lzSAuthUserAtom from '@lz-atoms/Auth/user';
import LzSAppBasics from '@lz-app/AppBasics';
import LzMOption from '@lz-models/commons/Option';
import LzMUser from '@lz-models/commons/User';
import { lzFNotificationsShowError, lzFNotificationsShowSuccess } from '@lz-components/commons/Notifications';
import { LzTNavItems } from '@lz-layouts/Navigation/types';
import { LzTMediaStateDataParams, LzTOpenObject } from '@lz/typings/types';
import { lzFKebabToSnakeCase } from './String';

export const lzFGetFormattedAmount = (value: number, allowDecimals?: boolean, noCurrencySymbol?: boolean): string => {
  const input = allowDecimals ? value : Math.round(value);

  // its sad that en-IN works to show caror-lakh instead of billion-million, but en-PK does not 

  if (noCurrencySymbol) {
    return new Intl.NumberFormat('en-IN', { style: 'decimal' })
      .format(input)
      .replace('.00', '');
  }

  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'PKR' })
    .format(input)
    .replace('PKR', 'Rs')
    .replace('.00', '');
};

export const lzFGetHashFromObjectArray = (objArr: LzTOpenObject[], idField?: string, nameField?: string): LzTOpenObject => {
  const theIdField = idField || 'id';
  const theNameField = nameField || 'name';
  const out: LzTOpenObject = {};

  for (const obj of objArr) {
    out[obj[theIdField]] = obj[theNameField];
  }

  return out;
};

export const lzFGetOptionsFromHashArray = (hash: any): LzMOption[] => {
  const out: LzMOption[] = [];

  for (const key in hash) {
    out.push(new LzMOption({ value: key, text: hash[key] }));
  }

  return out;
};

export const lzFLogInfo = (...args: any[]): void => {
  if (LzSAppBasics.debug_mode_enabled) {
    console.log(...args);
  }
};

export const lzFDownloadBlobFile = (blob: string | Blob, filename?: string): void => {
  saveAs(blob, filename);
};

export const lzFValueTagsDisplay = (val: string): string => {
  let out = '<small><b>{site-base-url}</b>';

  if (val) {
    out += ', <b>{' + val.split(',').join('}</b>, <b>{') + '}</b>';
  }

  out +=
    '<br/>use above place holders where you want to display respective data in subject and body</small>';

  return out;
};

export const lzFShowErrorMessage = (setCommonsLastErrorMessageState: any, msg: string): void => {
  setCommonsLastErrorMessageState(msg);
  lzFNotificationsShowError(msg);
};

export const useLzFShowErrorMessage = () => {
  const setCommonsLastErrorMessageState = useSetAtom(lzSCommonsLastErrorMessageAtom);

  return (msg: string) =>
    lzFShowErrorMessage(setCommonsLastErrorMessageState, msg);
};

export const lzFShowSuccessMessage = (msg: string): void => {
  lzFNotificationsShowSuccess(msg);
};

export const lzFSortByPosition = (objOfObjs: any, sortKey: string): any => {
  const order: any = [],
    res: any = {};

  Object.keys(objOfObjs).forEach((key) => {
    return (order[objOfObjs[key][sortKey] - 1] = key);
  });

  order.forEach((key: string | number) => {
    res[key] = objOfObjs[key];
  });

  return res;
};

export const lzFSortArrayOfObjectsByPosition = (inputArr: any[], sortKey: string): any[] => {
  return inputArr.slice().sort((a, b) => a[sortKey] - b[sortKey]);
};

/**
 * Position an object's element to a place specified by a number.
 *
 * @param obj The input object to process.
 * @param key The element to move to a position.
 * @param pos The position where to move the element (0 based).
 *
 * @return The updated object.
 */
export const lzFPositionObjectElementTo = (obj: any, key: string, pos: number) => {
  const out: LzTOpenObject = {};

  let runningPos = 0;
  let elementInserted = false;
  for (const runningKey in obj) {
    // If this is the element then skip it because we are positioning it manually (unless surprisingly this is the position we want it on)
    if (runningKey === key && runningPos !== pos) {
      continue;
    }

    // If this is the position where the element should be then put it there
    if (runningPos === pos) {
      out[key] = obj[key];
      elementInserted = true;
      runningPos++;
    }

    // Put the running element in the output object
    out[runningKey] = obj[runningKey];
    runningPos++;
  }

  // If by chance the element was not inserted (could be because `pos` is higher than available positions)
  if (!elementInserted) {
    out[key] = obj[key];
  }

  return out;
};

export const lzFBytesToSize = (bytes: number): string => {
  var sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Byte';
  var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)) + '');
  return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i];
};

export const lzFShowMediaBrowser = (
  setCommonsMediaBrowserDataState: any,
  params: LzTMediaStateDataParams,
  successCallback?: CallableFunction,
  failureCallback?: CallableFunction
): void => {
  setCommonsMediaBrowserDataState({
    params: params,
    successCallback:
      typeof successCallback === 'function'
        ? successCallback
        : (media: any) => console.log('MEDIA-SELECTED', media),
    failureCallback:
      typeof failureCallback === 'function'
        ? failureCallback
        : (msg: string) => console.error('FAILED-UPLOAD', msg),
  });
};

export const useLzFShowMediaBrowser = () => {
  const setCommonsMediaBrowserDataState = useSetAtom(lzSCommonsMediaBrowserDataAtom);

  return (
    params: LzTMediaStateDataParams,
    successCallback?: CallableFunction,
    failureCallback?: CallableFunction
  ) =>
    lzFShowMediaBrowser(
      setCommonsMediaBrowserDataState,
      params,
      successCallback,
      failureCallback
    );
};

export const lzFHideMediaBrowser = (setCommonsMediaBrowserDataState: any): void => {
  setCommonsMediaBrowserDataState(null);
};

export const useLzFHideMediaBrowser = () => {
  const setCommonsMediaBrowserDataState = useSetAtom(lzSCommonsMediaBrowserDataAtom);

  return () => lzFHideMediaBrowser(setCommonsMediaBrowserDataState);
};

export const lzFGetCurrentRoutePathSegments = (): Array<string> => {
  // TODO: Handle the case of a different type of routing then Hash Routing
  return window.location.hash
    .split('/')
    .filter((x) => x)
    .slice(1);
};

export const lzFGetCurrentRoute = (): string => {
  return '/' + lzFGetCurrentRoutePathSegments().join('/');
};

export const lzFGetCurrentRouteEquivOfKeyBreadcrumb = (): string => {
  const routeSegments = lzFGetCurrentRoutePathSegments();
  const keySegments: Array<string> = [];

  for (const dex in routeSegments) {
    keySegments.push(lzFKebabToSnakeCase(routeSegments[dex]));
  }

  return keySegments.join('.');
};

/**
 * Helper hook to load a script for a component.
 *
 * @param url Location of the script to be loaded
 */
export const useLzFJavaScript = (url: string): void => {
  useEffect(() => {
    const script = document.createElement('script');

    script.src = url;
    script.async = true;

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [url]);
};

export const useLzFUrlQueryParams = () => {
  const location = useLocation();

  return () => {
    const out: LzTOpenObject = {};
    const qs = new URLSearchParams(location.search);
    qs.forEach((val, key) => (out[key] = val));
    return out;
  };
};

/**
 * Get sub-links of a nav item based on the logged in user.
 *
 * @param navItems
 * @param parentKey
 *
 * @returns array
 */
export const lzFGetNavSubLinks = (navItems: LzTNavItems, parentKey: string): LzTNavItems => {
  for (const item of navItems) {
    if (item.key === parentKey) {
      if (!item.children || item.children.length < 1) {
        return [];
      }

      const out: LzTNavItems = [];
      for (const subitem of item.children) {
        out.push(subitem);
      }

      return out;
    }
  }

  return [];
};

export const lzFDivide = (num: number, denom: number): number => {
  if (denom <= 0) {
    return num;
  }
  return num / denom;
};

export const useLzFCheckRights = () => {
  const authUserState = useAtomValue(lzSAuthUserAtom);

  if (isNull(authUserState)) {
    return (_: string): boolean => {
      console.error('User state not loaded yet!');
      return false;
    };
  }

  const user = new LzMUser(authUserState);

  return (rightsPath: string): boolean => {
    return user.checkRights(rightsPath);
  };
};

export const lzFGetFunctionName = () => {
  // TODO: Find a way to get this, currently no way
  return 'n/a';
};

const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];

const zero = "Zero";
const arab = "Arab";
const crore = "Crore";
const lakh = "Lakh";
const trillion = "Trillion";
const billion = "Billion";
const million = "Million";
const thousand = "Thousand";
const hundred = "Hundred";
const currency = "Rupees";
const paisa = "Paisa";
const only = "Only";

export const lzFGetAmountInWords = (value: number, skipPaisa?: boolean, doPak?: boolean): string => {
  const amount = +(value).toFixed(2).toString().replace('.00', '');

  if (0 === amount) return `${zero} ${currency} ${only}`;

  function convert(num: number): string {
    return doPak ? convertPak(num) : convertIntl(num);
  }

  function convertPak(num: number): string {
    let parts = [];
    if (num >= 1e9) {
      parts.push(`${convert(Math.floor(num / 1e9))} ${arab}`);
      num %= 1e9;
    }
    if (num >= 1e7) {
      parts.push(`${convert(Math.floor(num / 1e7))} ${crore}`);
      num %= 1e7;
    }
    if (num >= 1e5) {
      parts.push(`${convert(Math.floor(num / 1e5))} ${lakh}`);
      num %= 1e5;
    }
    if (num >= 1000) {
      parts.push(`${convert(Math.floor(num / 1000))} ${thousand}`);
      num %= 1000;
    }
    if (num >= 100) {
      parts.push(`${convert(Math.floor(num / 100))} ${hundred}`);
      num %= 100;
    }
    if (num >= 20) {
      parts.push(`${tens[Math.floor(num / 10)]}`);
      if (num % 10 > 0) parts.push(units[num % 10]);
    } else if (num >= 10) {
      parts.push(`${teens[num - 10]}`);
    } else if (num > 0) {
      parts.push(`${units[num]}`);
    }
    return parts.join(" ");
  }

  function convertIntl(num: number): string {
    let parts = [];
    if (num >= 1e12) {
      parts.push(`${convert(Math.floor(num / 1e12))} ${trillion}`);
      num %= 1e12;
    }
    if (num >= 1e9) {
      parts.push(`${convert(Math.floor(num / 1e9))} ${billion}`);
      num %= 1e9;
    }
    if (num >= 1e6) {
      parts.push(`${convert(Math.floor(num / 1e6))} ${million}`);
      num %= 1e6;
    }
    if (num >= 1000) {
      parts.push(`${convert(Math.floor(num / 1000))} ${thousand}`);
      num %= 1000;
    }
    if (num >= 100) {
      parts.push(`${convert(Math.floor(num / 100))} ${hundred}`);
      num %= 100;
    }
    if (num >= 20) {
      parts.push(`${tens[Math.floor(num / 10)]}`);
      if (num % 10 > 0) parts.push(units[num % 10]);
    } else if (num >= 10) {
      parts.push(`${teens[num - 10]}`);
    } else if (num > 0) {
      parts.push(`${units[num]}`);
    }
    return parts.join(" ");
  }

  let integerPart = Math.floor(amount);
  let wholeWordPart = convert(integerPart);
  let result = wholeWordPart ? `${wholeWordPart} ${currency}` : '';

  let decimalPart = Math.round((amount - integerPart) * 100);
  if (decimalPart > 0 && !skipPaisa) {
    if (wholeWordPart) {
      result += " and ";
    }
    result += `${convert(decimalPart)} ${paisa}`;
  }

  return `${result} ${only}`;
};

export const lzFGetAppRouteFullUrl = (route?: string): string => {
  const theRoute = route || '';
  return LzSAppBasics.app_base_url + '#' + ('/' === theRoute.charAt(0) ? '' : '/') + theRoute;
};

export const lzFCopyToClipboardUnsecured = (text: string, notify?: boolean | string): boolean => {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand('copy');
    document.body.removeChild(textArea);
    if (typeof notify === 'string') {
      lzFShowSuccessMessage(notify);
    } else if (notify === true) {
      lzFShowSuccessMessage('Copied to clipboard!');
    }
    return true;
  } catch (err) {
    console.error('Failed to copy to text x2: ', err);
    document.body.removeChild(textArea);
    return false;
  }
};

/**
 * Copy text to clipbboard.
 * 
 * @param text Text to copy.
 * @param notify If true then show a stock message, if string then show that as message.
 * 
 * @returns true if succeeded, false if failed (with error in console log).
 */
export const lzFCopyToClipboard = async (text: string, notify?: boolean | string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    if (typeof notify === 'string') {
      lzFShowSuccessMessage(notify);
    } else if (notify === true) {
      lzFShowSuccessMessage('Copied to clipboard!');
    }
    return true;
  } catch (err) {
    console.error('Failed to copy text x1: ', err);
    return lzFCopyToClipboardUnsecured(text, notify);
  }
};
